import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import comprobanteService from "../../services/comprobanteService";
import comprobantePagoService from "../../services/comprobantePagoService";
import { init, setEntity } from "../../redux/receiptSlice";
import { Grid, Card, CardContent, Box, Snackbar, Alert } from '@mui/material';
import { formatoMonto } from '../../components/common/MontoTextField';
import ReceiptDetail from '../../components/ReceiptDetail'
import ReceiptPayment from '../../components/ReceiptPayment'
import MantenimientoForm from '../../components/common/MantenimientoForm';
import { useMantenimiento } from '../../hooks/useMantenimiento';

// Opcion del menu para registrar pagos parciales de un comprobante ya guardado.
// Tambien se abre embebido en un dialogo (desde la navegabilidad de los periodos
// de terapia), en cuyo caso el identificador llega por prop y la pantalla que lo
// abrio recibe el aviso del pago registrado.
const Payment = (props) => {
    const receipt = useSelector((state) => state.receipt["Normal"]?.entity);
    const [receiptPayments, setReceiptPayments] = useState([]);
    // Al registrar, la lista se vuelve a cargar para que los pagos recién
    // guardados se muestren como registrados, con su estado y sin acciones.
    const [claveLista, setClaveLista] = useState(0);
    const [openMessage, setOpenMessage] = useState(false)
    const onCloseMessage = () => { setOpenMessage(!openMessage); }
    const [propsMessage, setPropsMessage] = useState({ severity: "success", message: "" });
    const dispatch = useDispatch();
    const params = useParams();
    const navigate = useNavigate();
    const { isReadOnly, setIsReadOnly } = useMantenimiento("comprobante");

    // Como pagina el comprobante viene por la ruta; en dialogo, por prop.
    const idComprobante = props.idComprobante != null ? props.idComprobante : params.id;

    const serieNumero = receipt?.serie && receipt?.numero
        ? receipt.serie + "-" + String(receipt.numero).padStart(6, "0")
        : "";

    useEffect(() => {
        // El slice se inicializa aqui porque el componente Receipt no se monta en esta pantalla.
        dispatch(init({ sliceKey: "Normal" }));
    }, [dispatch]);

    const fetchEntity = useCallback(async () => {
        if (!idComprobante) {
            // En dialogo no hay a donde volver: la ventana se cierra sola.
            if (!props.enDialogo) navigate("/receipt");
            return;
        }

        const response = await comprobanteService.getComprobanteView(idComprobante)
            .catch((err) => { console.log("Err: ", err); });

        if (response?.data) {
            dispatch(setEntity({ sliceKey: "Normal", data: response.data.data }));
        }
    }, [idComprobante, navigate, props.enDialogo, dispatch]);

    useEffect(() => {
        fetchEntity();
    }, [fetchEntity]);

    const postEntity = async () => {
        const total = parseFloat(receipt?.montoTotal) || 0;
        const totalPagos = (receiptPayments || []).reduce((sum, item) => {
            if (Number(item.idEstado) === 4 || Number(item.idEstado) === 146 || item.estado === "Anulado") return sum;
            return sum + (parseFloat(item.monto) || 0);
        }, 0);

        if (total > 0 && totalPagos > total + 0.009) {
            setPropsMessage({ severity: "error", message: `El total de pagos (${formatoMonto(totalPagos)}) no puede ser mayor al total del comprobante (${formatoMonto(total)})` });
            setOpenMessage(true);
            return;
        }

        await comprobantePagoService.registrarPagosComprobante(idComprobante, receiptPayments)
            .then(() => {
                setPropsMessage({ severity: "success", message: "Se registraron los pagos con exito" })
                setOpenMessage(true);
                setIsReadOnly(true);
                fetchEntity();
                setClaveLista((valor) => valor + 1);

                // En dialogo se avisa a la pantalla que lo abrio para que refresque.
                if (props.onSaved) props.onSaved(parseInt(idComprobante));
            })
            .catch((error) => {
                const msg = error?.response?.data?.detail || error?.response?.data?.Detail || "Hubo un error al registrar el pago";
                setPropsMessage({ severity: "error", message: msg })
                setOpenMessage(true);
            });
    }

    const pagoAnulado = () => {
        fetchEntity();
        setClaveLista((valor) => valor + 1);
        if (props.onSaved) props.onSaved(parseInt(idComprobante));
    };

    // Los pagos solo se pueden registrar si el comprobante no esta validado (consolidado) ni anulado.
    const comprobanteBloqueado = Number(receipt?.idEstado) === 4
        || Number(receipt?.idEstadoPago) === 227
        || (Array.isArray(receiptPayments) && receiptPayments.some((p) => p && Number(p.idEstado) === 227));
    const soloLecturaPagos = isReadOnly || comprobanteBloqueado;

    // Un comprobante cancelado o sin saldo pendiente no admite registrar nuevos pagos
    const saldoPendiente = Math.round(((parseFloat(receipt?.montoSaldo) || 0) + Number.EPSILON) * 100) / 100;
    const esCancelado = Number(receipt?.idEstadoPago) === 145 || Number(receipt?.idEstadoPago) === 227 || saldoPendiente <= 0;
    const puedeAgregarPago = !soloLecturaPagos && !esCancelado && saldoPendiente > 0;

    return (
        <div>
            <MantenimientoForm
                title={(puedeAgregarPago ? "Registrar Pago" : "Ver Pagos") + (serieNumero ? " - " + serieNumero : "")}
                backTo={props.enDialogo ? null : "/receipt"}
                onSave={postEntity}
                showSave={puedeAgregarPago}
                disabled={!puedeAgregarPago || (receiptPayments.filter(p => !p.idComprobante && !p.id).length === 0)}
                saveText="Registrar"
            >
                <Grid container spacing={1} sx={{
                    "& .MuiGrid-root>.MuiGrid-item": {
                        paddingTop: "4px"
                    },
                }}>
                    {/* Los items del comprobante: el cobro que se esta pagando. */}
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
                                <ReceiptDetail
                                    sliceKey={"Normal"}
                                    esNuevo={false}
                                    isReadOnly={true}
                                    idComprobante={idComprobante}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <Card>
                            <CardContent sx={{ padding: "4px 8px 6px 8px !important" }}>
                                <Box sx={{ fontWeight: 'bold', paddingBottom: 0.5 }}>
                                    Total: {formatoMonto(receipt?.montoTotal || 0)} | Saldo: {formatoMonto(receipt?.montoSaldo || 0)}
                                </Box>
                                <ReceiptPayment
                                    key={claveLista}
                                    sliceKey={"Normal"}
                                    setPayments={setReceiptPayments}
                                    esNuevo={false}
                                    isReadOnly={soloLecturaPagos}
                                    permitirAgregar={puedeAgregarPago}
                                    idComprobante={idComprobante}
                                    onPaymentAnnulled={pagoAnulado}
                                />
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </MantenimientoForm>
            <Snackbar open={openMessage} autoHideDuration={5000} onClose={onCloseMessage}
                anchorOrigin={{vertical: "top", horizontal: "center"}}>
                <Alert onClose={onCloseMessage} severity={propsMessage.severity}>
                    {propsMessage.message}
                </Alert>
            </Snackbar>
        </div>
    )
}

export default Payment
